// Shared helpers for gatepass duration vs security clearance expiry validation.

export const toStartOfDay = (value: Date): Date => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const parseDateOnlyAsLocal = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const [year, month, day] = trimmed.slice(0, 10).split("-").map(Number);

  if (![year, month, day].some(Number.isNaN)) {
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseDateValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const isDateOnOrAfterToday = (value: unknown, referenceDate: Date = new Date()): boolean => {
  const parsed = value instanceof Date ? value : parseDateOnlyAsLocal(value);
  if (!parsed) return false;
  return toStartOfDay(parsed).getTime() >= toStartOfDay(referenceDate).getTime();
};

export const parseDdMmYyyyDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = new RegExp(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/).exec(trimmed);
  if (match) {
    const [, day, month, year] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return parseDateValue(trimmed);
};

const addMonthsCappedToEndOfMonth = (date: Date, months: number): Date => {
  const result = new Date(date);
  const originalDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  return result;
};

const subtractDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

export const computeGatepassExpiryDate = (
  expectedEntryDate: unknown,
  gatepassDuration: string,
  permanentDuration?: string,
): Date | null => {
  const start = parseDateValue(expectedEntryDate);
  if (!start) return null;

  const normalizedDuration = String(gatepassDuration ?? "")
    .trim()
    .toLowerCase();

  if (normalizedDuration === "daily") {
    return new Date(start);
  }

  if (normalizedDuration === "weekly") {
    const expiry = new Date(start);
    expiry.setDate(expiry.getDate() + 6);
    return expiry;
  }

  if (normalizedDuration === "permanent") {
    const months = Number(permanentDuration ?? "0");
    if (!Number.isFinite(months) || months <= 0) return null;
    return subtractDays(addMonthsCappedToEndOfMonth(start, months), 1);
  }

  return null;
};

export const gatepassExpiryExceedsScValidity = (
  gatepassExpiryDate: Date | null,
  securityClearanceValidity: unknown,
): boolean => {
  if (!gatepassExpiryDate) return false;
  const scExpiry = parseDdMmYyyyDate(securityClearanceValidity);
  if (!scExpiry) return false;
  return toStartOfDay(gatepassExpiryDate).getTime() > toStartOfDay(scExpiry).getTime();
};
